import type { AnyValue } from "@/lib/rowTypes";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface PlayerLevel {
  total_xp: number;
  current_level: number;
  current_title: string;
  xp_for_next_level: number;
  xp_into_current_level: number;
}

const TITLE_LADDER = [
  { level: 1, title: "Rookie Saver" },
  { level: 3, title: "Budget Explorer" },
  { level: 6, title: "Cash Flow Learner" },
  { level: 9, title: "Wealth Builder" },
  { level: 12, title: "Market Explorer" },
  { level: 16, title: "Opportunity Hunter" },
  { level: 20, title: "Risk Strategist" },
  { level: 25, title: "Asset Architect" },
  { level: 32, title: "Legacy Builder" },
  { level: 40, title: "Capital Commander" },
  { level: 50, title: "HeirWise Master" },
];

function calcXpForLevel(level: number): number {
  // XP needed to go FROM level to level+1
  return 100 + (level - 1) * 50;
}

function calcLevelFromXp(totalXp: number): { level: number; xpInto: number; xpNeeded: number } {
  let level = 1;
  let running = 0;
  while (true) {
    const needed = calcXpForLevel(level);
    if (running + needed > totalXp) {
      return { level, xpInto: totalXp - running, xpNeeded: needed };
    }
    running += needed;
    level++;
  }
}

export function usePlayerProgression() {
  const { user } = useAuth();
  const [playerLevel, setPlayerLevel] = useState<PlayerLevel>({
    total_xp: 0,
    current_level: 1,
    current_title: "Rookie Saver",
    xp_for_next_level: 100,
    xp_into_current_level: 0,
  });
  const [loading, setLoading] = useState(true);
  const [lastAward, setLastAward] = useState<{ xp: number; leveledUp: boolean; newTitle?: string } | null>(null);

  const fetchLevel = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("user_levels")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (data) {
      const { level, xpInto, xpNeeded } = calcLevelFromXp((data).total_xp);
      setPlayerLevel({
        total_xp: (data).total_xp,
        current_level: level,
        current_title: (data).current_title,
        xp_for_next_level: xpNeeded,
        xp_into_current_level: xpInto,
      });
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchLevel();
  }, [fetchLevel]);

  const awardXP = useCallback(
    async (amount: number, reason: string, sourceType = "lesson", sourceId?: string) => {
      if (!user || amount <= 0) return null;
      const { data, error } = await supabase.rpc("award_xp", {
        p_amount: amount,
        p_reason: reason,
        p_source_type: sourceType,
        p_source_id: sourceId || null,
      });
      if (error) {
        console.error("award_xp error:", error);
        return null;
      }
      const result = data;
      const { level, xpInto, xpNeeded } = calcLevelFromXp((result as AnyValue).total_xp);
      setPlayerLevel({
        total_xp: (result as AnyValue).total_xp,
        current_level: level,
        current_title: (result as AnyValue).title,
        xp_for_next_level: xpNeeded,
        xp_into_current_level: xpInto,
      });
      const award = {
        xp: amount,
        leveledUp: (result as AnyValue).leveled_up,
        newTitle: (result as AnyValue).leveled_up ? (result as AnyValue).title : undefined,
      };
      setLastAward(award);
      return award;
    },
    [user]
  );

  const clearLastAward = useCallback(() => setLastAward(null), []);

  return {
    playerLevel,
    loading,
    lastAward,
    awardXP,
    clearLastAward,
    refetch: fetchLevel,
    titleLadder: TITLE_LADDER,
  };
}
